import React, { useRef, useState } from "react";
import {
  AlertDialog, AlertDialogOverlay, AlertDialogContent, AlertDialogHeader, AlertDialogBody, AlertDialogFooter,
  Box, Button, Flex, Input, Link, Text, VStack, useToast,
} from "@chakra-ui/react";
import { Link as RouterLink } from "react-router-dom";
import { fetchWithAuth } from "../../utils/fetchWithAuth";
import { CLIENT_TOOLS, clientIconSrc, clientLabel } from "./clientTools";

const CONFIRM_WORD = "disconnect";
const connectionLabel = connection => connection?.display_name || clientLabel(connection?.client_name);

// AI tools connected to Pabs, each with its own private connection. Disconnecting
// asks the person to type the word first, so one stray click never cuts a tool off.
export default function ConnectedToolsList({ connections, onDisconnected, showPlan = false }) {
  const [pending, setPending] = useState(null);
  const [typed, setTyped] = useState("");
  const [busy, setBusy] = useState(false);
  const cancelRef = useRef(null);
  const toast = useToast();

  const close = () => { setPending(null); setTyped(""); };
  const disconnect = async () => {
    setBusy(true);
    try {
      const response = await fetchWithAuth(pending.kind==='plugin' ? `/api/plugins/connections/${pending.id}/revoke` : `/api/client-setup/${pending.id}/revoke`, { method: "POST" });
      if (!response.ok) throw new Error();
      onDisconnected?.(pending.id);
      toast({ title: `${connectionLabel(pending)} disconnected`, description: "Your saved Pabs projects are unchanged.", status: "success", duration: 3000 });
      close();
    } catch {
      toast({ title: "Couldn’t disconnect", description: "Try again in a moment.", status: "error", duration: 3000 });
    } finally {
      setBusy(false);
    }
  };

  return (
    <>
      <VStack align="stretch" spacing={2}>
        {connections.map((connection) => {
          const tool = CLIENT_TOOLS.find((item) => item.id === connection.client_name);
          const since = new Date(connection.created_at).toLocaleDateString(undefined, { day: "numeric", month: "short" });
          return (
            <Flex key={connection.id} align="center" gap={3} px={4} py={3} border="1px solid" borderColor="gray.200" borderRadius="lg" wrap="wrap">
              {tool && <img src={clientIconSrc(tool)} alt="" width="20" height="20" style={{ width: 20, height: 20, objectFit: "contain" }} />}
              <Box flex="1" minW="160px">
                <Text fontSize="sm" fontWeight="600" color="gray.900">{connectionLabel(connection)}</Text>
                <Text fontSize="xs" color="gray.600">
                  {connection.kind==='plugin' ? `Account connection. ${connection.scope.includes('pabs:write')?'Read and update products':'Read only'}. Connected on ${since}` : showPlan && connection.plan_id ? (
                    <>
                      <Link as={RouterLink} to={`/plan/${connection.plan_id}`} textDecoration="underline">{connection.plan_name || "Plan"}</Link>
                      {connection.created_plan ? `. Set up on ${since}` : `. Connected on ${since}`}
                    </>
                  ) : (connection.created_plan ? `Set up this plan on ${since}` : `Connected on ${since}`)}
                  {connection.status === "authorized" ? ". Setup not finished yet" : ""}
                </Text>
              </Box>
              <Button size="sm" variant="outline" onClick={() => setPending(connection)}>Disconnect</Button>
            </Flex>
          );
        })}
      </VStack>

      <AlertDialog isOpen={Boolean(pending)} leastDestructiveRef={cancelRef} onClose={close} isCentered>
        <AlertDialogOverlay>
          <AlertDialogContent>
            <AlertDialogHeader fontSize="lg" fontWeight="700">
              Disconnect {pending ? connectionLabel(pending) : ""}?
            </AlertDialogHeader>
            <AlertDialogBody>
              <Text fontSize="sm" color="gray.700" lineHeight="1.6">
                {pending ? connectionLabel(pending) : "This tool"} will stop working with Pabs
                {pending?.plan_name ? ` on ${pending.plan_name}` : ""}. Your saved products stay as they are. To use Pabs there again, you’ll need to connect it again.
              </Text>
              <Text fontSize="sm" color="gray.700" mt={4} mb={2}>
                Type <b>{CONFIRM_WORD}</b> to confirm.
              </Text>
              <Input value={typed} onChange={(event) => setTyped(event.target.value)} placeholder={CONFIRM_WORD} autoComplete="off" />
            </AlertDialogBody>
            <AlertDialogFooter gap={2}>
              <Button ref={cancelRef} variant="ghost" onClick={close}>Keep connected</Button>
              <Button colorScheme="red" isLoading={busy} isDisabled={typed.trim().toLowerCase() !== CONFIRM_WORD} onClick={disconnect}>
                Disconnect
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
}
